import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Link2 } from "lucide-react";
import api, { formatApiError } from "../api";
import { useLang } from "../i18n";

const BE = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u && u.startsWith("/") ? `${BE}${u}` : u);

const inputCls =
  "w-full bg-[#0D0F16] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[rgba(68,153,247,0.5)] transition-colors";

export default function PhotoManager({ images, onChange, onUploadingChange }) {
  const { t } = useLang();
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);

  const setBusy = (v) => {
    setUploading(v);
    onUploadingChange?.(v);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post("/uploads", fd);
      onChange([...images, ...data.files.map((x) => x.url)]);
      toast.success(t("admin.uploaded"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    onChange([...images, u]);
    setUrlInput("");
  };

  return (
    <div data-testid="photo-manager">
      <span className="block text-[11px] font-mono-num tracking-widest uppercase text-slate-500 mb-1.5">{t("admin.photos")}</span>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {images.map((u, i) => (
          <div key={i} className="relative aspect-[16/10] rounded-xl overflow-hidden border border-white/10 group" data-testid={`vehicle-photo-${i}`}>
            <img src={imgSrc(u)} alt="" className="h-full w-full object-cover" />
            {i === 0 && (
              <span className="absolute bottom-1.5 left-1.5 text-[10px] font-mono-num tracking-widest uppercase bg-[#4499F7] text-[#0A0B10] rounded-full px-2 py-0.5">
                {t("admin.mainPhoto")}
              </span>
            )}
            <button
              type="button"
              data-testid={`vehicle-photo-remove-${i}`}
              onClick={() => onChange(images.filter((_, idx) => idx !== i))}
              className="absolute top-1.5 right-1.5 bg-black/70 text-slate-200 hover:text-red-400 rounded-full p-1 transition-colors"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <button
          type="button"
          data-testid="vehicle-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="aspect-[16/10] rounded-xl border border-dashed border-white/20 hover:border-[rgba(68,153,247,0.6)] text-slate-400 hover:text-[#4499F7] transition-colors flex flex-col items-center justify-center gap-1.5 text-xs disabled:opacity-60"
        >
          <ImagePlus size={20} />
          {uploading ? t("admin.uploading") : t("admin.uploadPhotos")}
        </button>
      </div>
      <input
        ref={fileRef}
        data-testid="vehicle-file-input"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        onChange={onFiles}
        className="hidden"
      />
      <div className="mt-3 flex gap-2">
        <input
          data-testid="vehicle-form-image-url"
          type="url"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder={t("admin.addImageUrl")}
          className={inputCls}
        />
        <button
          type="button"
          data-testid="vehicle-add-url-btn"
          onClick={addUrl}
          className="shrink-0 flex items-center gap-1.5 border border-white/15 text-slate-300 text-sm rounded-xl px-4 hover:border-[rgba(68,153,247,0.5)] hover:text-[#4499F7] transition-colors"
        >
          <Link2 size={14} />
          {t("admin.add")}
        </button>
      </div>
    </div>
  );
}
